import { routes } from '@aline/routes';
import { matchPath } from 'react-router-dom';

export const TAB_TYPES = {
  admin: 'admin',
  chat: 'chat',
  community: 'community',
  dashboard: 'dashboard',
  documentEditor: 'document-editor',
  documents: 'documents',
  getStarted: 'get-started',
  home: 'home',
  parties: 'parties',
  playbooks: 'playbooks',
  reminders: 'reminders',
  reports: 'reports',
  templates: 'templates',
  workflowDetail: 'workflow-detail',
  workflows: 'workflows',
} as const;

export type TabType = (typeof TAB_TYPES)[keyof typeof TAB_TYPES];

/**
 * Live tab state persisted in sessionStorage by TabProvider.
 *
 * `route` is the durable route owned by the tab (pathname + durable search/hash).
 * One-shot deep-link params live in `pendingParams` only — never in sessionStorage
 * or history snapshots.
 * `lastAccessed` is runtime metadata for ordering/focus decisions and is not
 * copied into browser history snapshots.
 */
export interface Tab {
  id: UUID;
  lastAccessed: number;
  pinned: boolean;
  route: string;
  title: string;
  type: TabType;
  /** One-shot deep-link params; runtime-only, never persisted. */
  pendingParams?: Record<string, string>;
}

export interface TabsState {
  activeTabId: UUID | null;
  tabs: Tab[];
}

interface RouteMatch {
  defaultTitle: string;
  patterns: string[];
  type: TabType;
}

/**
 * Order matters — more specific patterns must come before wildcards.
 * Route defaults are fallback titles; existing tab titles win during route sync.
 */
const ROUTE_MATCHES: RouteMatch[] = [
  {
    defaultTitle: 'Home',
    patterns: [routes.ChatIndex],
    type: TAB_TYPES.home,
  },
  {
    defaultTitle: 'Send for Signature',
    patterns: [routes.SendForSignatureWorkflow],
    type: TAB_TYPES.workflowDetail,
  },
  {
    defaultTitle: 'Launch Workflow',
    patterns: [routes.QuickStartLaunchWorkflow, routes.QuickStartPickWorkflowAction],
    type: TAB_TYPES.workflowDetail,
  },
  {
    defaultTitle: 'Launch Template',
    patterns: [
      routes.QuickStartLaunchTemplateIndex,
      routes.QuickStartLaunchTemplateSelectedTemplate,
    ],
    type: TAB_TYPES.templates,
  },
  { defaultTitle: 'Dashboard', patterns: [routes.DashboardIndex], type: TAB_TYPES.dashboard },
  // Workflow list pages
  {
    defaultTitle: 'Workflows',
    patterns: [
      routes.WorkflowList,
      routes.WorkflowBoard,
      routes.WorkflowInsights,
      routes.WorkflowIndexMustRedirect,
    ],
    type: TAB_TYPES.workflows,
  },
  // Workflow detail
  {
    defaultTitle: 'Workflow',
    patterns: [routes.WorkflowDetail],
    type: TAB_TYPES.workflowDetail,
  },
  // Template list pages
  {
    defaultTitle: 'Templates',
    patterns: [routes.TemplateIndex, routes.TemplatePublished, routes.TemplateDraft],
    type: TAB_TYPES.templates,
  },
  // Template detail
  {
    defaultTitle: 'Template',
    patterns: [routes.TemplateSelectedTemplateIndex],
    type: TAB_TYPES.templates,
  },
  // Documents list
  {
    defaultTitle: 'Documents',
    patterns: [routes.RepositoryIndex],
    type: TAB_TYPES.documents,
  },
  // Document editor
  {
    defaultTitle: 'Document',
    patterns: [routes.DocumentDetailWorkflowDetail, routes.DocumentDetail],
    type: TAB_TYPES.documentEditor,
  },
  // Parties list
  { defaultTitle: 'Parties', patterns: [routes.PartyIndex], type: TAB_TYPES.parties },
  // Party detail
  {
    defaultTitle: 'Party',
    patterns: [routes.PartyDetailIndex],
    type: TAB_TYPES.parties,
  },
  // Reports list
  { defaultTitle: 'Reports', patterns: [routes.ReportIndex], type: TAB_TYPES.reports },
  // Report detail
  {
    defaultTitle: 'Report',
    patterns: [routes.ReportDetail],
    type: TAB_TYPES.reports,
  },
  {
    defaultTitle: 'Playbook',
    patterns: [routes.PlaybooksDetailRule, routes.PlaybooksDetail],
    type: TAB_TYPES.playbooks,
  },
  { defaultTitle: 'Playbooks', patterns: [routes.PlaybooksIndex], type: TAB_TYPES.playbooks },
  // Reminders
  {
    defaultTitle: 'Reminders',
    patterns: [routes.RemindersIndex],
    type: TAB_TYPES.reminders,
  },
  // Admin sub-pages — keep type as `admin` so tab grouping stays stable; only
  // the default title differs. Specific patterns must come before AdminIndex
  // because `getTabInfoFromRoute` returns the first match.
  { defaultTitle: 'User', patterns: [routes.AdminUserDetail], type: TAB_TYPES.admin },
  { defaultTitle: 'Users', patterns: [routes.AdminUserIndex], type: TAB_TYPES.admin },
  { defaultTitle: 'Group', patterns: [routes.AdminGroupDetail], type: TAB_TYPES.admin },
  { defaultTitle: 'Groups', patterns: [routes.AdminGroupIndex], type: TAB_TYPES.admin },
  { defaultTitle: 'Roles', patterns: [routes.AdminRoles], type: TAB_TYPES.admin },
  {
    defaultTitle: 'Contract Types',
    patterns: [routes.AdminContractTypes],
    type: TAB_TYPES.admin,
  },
  { defaultTitle: 'Integrations', patterns: [routes.AdminIntegrations], type: TAB_TYPES.admin },
  { defaultTitle: 'API Keys', patterns: [routes.AdminApiKeys], type: TAB_TYPES.admin },
  { defaultTitle: 'Billing', patterns: [routes.AdminBilling], type: TAB_TYPES.admin },
  { defaultTitle: 'Usage', patterns: [routes.AdminUsage], type: TAB_TYPES.admin },
  {
    defaultTitle: 'Upgrade Requests',
    patterns: [routes.AdminUpgradeRequests],
    type: TAB_TYPES.admin,
  },
  {
    defaultTitle: 'Data Migration',
    patterns: [routes.AdminDataMigration],
    type: TAB_TYPES.admin,
  },
  { defaultTitle: 'Org', patterns: [routes.AdminOrgIndex], type: TAB_TYPES.admin },
  { defaultTitle: 'Profile', patterns: [routes.AdminUserProfile], type: TAB_TYPES.admin },
  {
    defaultTitle: 'Admin & Settings',
    patterns: [routes.AdminIndex],
    type: TAB_TYPES.admin,
  },
  {
    defaultTitle: 'Community',
    patterns: [routes.CommunityIndex],
    type: TAB_TYPES.community,
  },
  {
    defaultTitle: 'Get Started',
    patterns: [routes.GetStarted],
    type: TAB_TYPES.getStarted,
  },
];

interface TabInfo {
  defaultTitle: string;
  type: TabType;
}

export function getTabInfoFromRoute(pathname: string): TabInfo {
  for (const { defaultTitle, patterns, type } of ROUTE_MATCHES) {
    for (const pattern of patterns) {
      if (matchPath({ end: false, path: pattern }, pathname)) {
        return { defaultTitle, type };
      }
    }
  }
  return { defaultTitle: 'Home', type: TAB_TYPES.home };
}

/**
 * Returns the "base type" for a tab type — used to check if a left-nav section
 * already has a tab open. For example, 'workflow-detail' maps to 'workflows'.
 */
export function getBaseTabType(type: TabType): TabType {
  if (type === TAB_TYPES.workflowDetail) return TAB_TYPES.workflows;
  if (type === TAB_TYPES.documentEditor) return TAB_TYPES.documents;
  if (type === TAB_TYPES.chat) return TAB_TYPES.home;
  return type;
}

export const DEFAULT_TAB_ROUTE = routes.ChatIndex;

// ---------------------------------------------------------------------------
// Browser history snapshots
// ---------------------------------------------------------------------------

/**
 * Lightweight tab data stored per browser history entry.
 *
 * This lets Back/Forward restore the tab arrangement that existed for that
 * history entry. `lastAccessed` is intentionally omitted because it should be
 * recalculated from the entry being restored, not persisted historically.
 */
export interface TabSnapshot {
  id: UUID;
  pinned: boolean;
  route: string;
  title: string;
  type: TabType;
}

export interface TabsSnapshot {
  activeTabId: UUID | null;
  tabs: TabSnapshot[];
}

/** Strips `lastAccessed` and `pendingParams` from each tab to create a serializable snapshot. */
export function createTabsSnapshot(state: TabsState): TabsSnapshot {
  return {
    activeTabId: state.activeTabId,
    tabs: state.tabs.map(({ id, pinned, route, title, type }) => ({
      id,
      pinned,
      route,
      title,
      type,
    })),
  };
}

/** Drops runtime-only fields before writing tabs to sessionStorage. */
export function tabsStateForPersist(state: TabsState): TabsState {
  return {
    activeTabId: state.activeTabId,
    tabs: state.tabs.map(({ pendingParams: _pendingParams, ...tab }) => tab),
  };
}

function getIsValidTabsSnapshot(value: unknown): value is TabsSnapshot {
  if (typeof value !== 'object' || value === null) return false;
  if (!('activeTabId' in value) || !('tabs' in value)) return false;
  if (!Array.isArray(value.tabs)) return false;
  return true;
}

/**
 * Reads the full tabs snapshot from the current browser history entry.
 * React Router 6 stores user-provided `state` under `window.history.state.usr`.
 */
export function readTabsSnapshotFromHistory(): TabsSnapshot | null {
  try {
    const usr = window.history.state?.usr;
    if (getIsValidTabsSnapshot(usr?.alineTabsSnapshot)) {
      return usr.alineTabsSnapshot;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Backward-compat reader for old history entries that only stored `alineTabId`.
 */
export function readTabIdFromHistory(): UUID | null {
  try {
    const usr = window.history.state?.usr;
    if (typeof usr?.alineTabId === 'string') return usr.alineTabId;
    if (typeof window.history.state?.alineTabId === 'string') {
      return window.history.state.alineTabId;
    }
    return null;
  } catch {
    return null;
  }
}
